import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DECLINE_REASON_MAX_LENGTH,
  DECLINE_REASON_MIN_LENGTH
} from '@core/models/domain/child-deactivation.domain';
import {
  DIFFERENT_APP_EXEMPTION_CAVEAT,
  exemptionReasonTakesNote,
  SCHOOL_USER_EXEMPTION_REASON_LABELS,
  SchoolUserExemptionReason
} from '@core/models/domain/account.domain';

/** Which decision the admin is confirming; each one asks for something different. */
export type DeactivationDecisionKind = 'DECLINE' | 'EXEMPT';

export interface DeactivationDecisionDialogData {
  kind: DeactivationDecisionKind;
  pupilName: string;
}

export interface DeactivationDecisionResult {
  kind: DeactivationDecisionKind;
  /** Set for DECLINE: the reason the parent will be told. */
  reason?: string;
  /** Set for EXEMPT. */
  exemptionReason?: SchoolUserExemptionReason;
  /** Set for EXEMPT only when the reason takes a note (Other, Different app). */
  exemptionNote?: string;
}

/** Only "Other" and "Different app" take a note, matching the pupil edit screen. */
const EXEMPTION_NOTE_MAX_LENGTH = 200;

/**
 * Collects what a decision needs before it is sent.
 *
 * <p>Declining owes the parent a reason and exempting owes the school's records one, so neither can
 * ride the plain confirm dialog, which has no input. Approving still does: it needs confirmation,
 * not information.</p>
 */
@Component({
  selector: 'app-deactivation-decision-dialog',
  templateUrl: './deactivation-decision-dialog.component.html',
  styleUrl: './deactivation-decision-dialog.component.scss',
  standalone: false,
})
export class DeactivationDecisionDialogComponent {
  readonly exemptionReasons = Object.values(SchoolUserExemptionReason);
  readonly exemptionReasonLabels = SCHOOL_USER_EXEMPTION_REASON_LABELS;
  readonly minReasonLength = DECLINE_REASON_MIN_LENGTH;
  readonly maxReasonLength = DECLINE_REASON_MAX_LENGTH;
  readonly maxNoteLength = EXEMPTION_NOTE_MAX_LENGTH;

  reason = '';
  exemptionReason: SchoolUserExemptionReason | null = null;
  exemptionNote = '';
  submitted = false;

  constructor(
    public dialogRef: MatDialogRef<DeactivationDecisionDialogComponent, DeactivationDecisionResult>,
    @Inject(MAT_DIALOG_DATA) public data: DeactivationDecisionDialogData
  ) {}

  get isDecline(): boolean {
    return this.data.kind === 'DECLINE';
  }

  get trimmedReason(): string {
    return (this.reason || '').trim();
  }

  get trimmedNote(): string {
    return (this.exemptionNote || '').trim();
  }

  get showExemptionNote(): boolean {
    return exemptionReasonTakesNote(this.exemptionReason);
  }

  /** DIGI-1865: "Different app" is the family's word, so the school is told LOQT does not check it. */
  get exemptionReasonCaveat(): string {
    return this.exemptionReason === SchoolUserExemptionReason.DIFFERENT_APP ? DIFFERENT_APP_EXEMPTION_CAVEAT : '';
  }

  /** Shown under the field once the admin has tried to submit, so it does not nag while typing. */
  get reasonError(): string {
    if (!this.submitted || !this.isDecline) {
      return '';
    }
    if (this.trimmedReason.length < this.minReasonLength) {
      return `Please give the parent a reason of at least ${this.minReasonLength} characters.`;
    }
    if (this.trimmedReason.length > this.maxReasonLength) {
      return `Please keep the reason under ${this.maxReasonLength} characters.`;
    }
    return '';
  }

  get noteError(): string {
    if (!this.submitted || this.isDecline) {
      return '';
    }
    if (!this.exemptionReason) {
      return 'Please choose an exemption reason.';
    }
    if (this.showExemptionNote && this.trimmedNote.length > this.maxNoteLength) {
      return `Please keep the comment under ${this.maxNoteLength} characters.`;
    }
    return '';
  }

  isValid(): boolean {
    if (this.isDecline) {
      return this.trimmedReason.length >= this.minReasonLength
        && this.trimmedReason.length <= this.maxReasonLength;
    }
    if (!this.exemptionReason) {
      return false;
    }
    return !this.showExemptionNote || this.trimmedNote.length <= this.maxNoteLength;
  }

  confirm(): void {
    this.submitted = true;
    if (!this.isValid()) {
      return;
    }
    if (this.isDecline) {
      this.dialogRef.close({ kind: 'DECLINE', reason: this.trimmedReason });
      return;
    }
    this.dialogRef.close({
      kind: 'EXEMPT',
      exemptionReason: this.exemptionReason as SchoolUserExemptionReason,
      // A note on any other reason would be dropped by the backend, so it is not sent at all.
      exemptionNote: this.showExemptionNote ? (this.trimmedNote || undefined) : undefined,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
